export type GestureAction = "swipe-left" | "swipe-right" | "long-press";

export function buildGestureHandler(
  onGesture: (gesture: GestureAction) => void | Promise<void>
) {
  let startX = 0;
  let startTime = 0;
  return {
    getContext: () => {
      return {
        startX,
        startTime,
      };
    },
    onTouchStart: (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startTime = Date.now();
    },
    onTouchEnd: async (e: TouchEvent) => {
      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - startX;
      const duration = Date.now() - startTime;
      if (Math.abs(deltaX) > 100 && duration < 1000) {
        if (deltaX > 0) {
          await onGesture("swipe-right");
        } else {
          await onGesture("swipe-left");
        }
      } else if (duration >= 1000 && duration <= 4000) {
        await onGesture("long-press");
      }
    },
  };
}

export class InvalidSequenceException extends Error {
  constructor() {
    super("Invalid sequence: sequence is empty");
  }
}

export function buildGestureSequenceHandler(
  expectedSequence: GestureAction[],
  onTrigger: () => void | Promise<void>,
  options?: {
    resetTimeoutMs?: number;
  }
) {
  if (expectedSequence.length === 0) {
    throw new InvalidSequenceException();
  }
  let currentStep = 0;

  let timer: ReturnType<typeof setTimeout> | null = null;

  const resetTimeoutMs = options?.resetTimeoutMs ?? 3_000;

  function resetState() {
    currentStep = 0;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  async function registerGesture(gesture: GestureAction) {
    if (currentStep > expectedSequence.length) {
      resetState();
      return;
    }

    if (expectedSequence[currentStep] !== gesture) {
      resetState();
      return;
    }

    currentStep++;

    if (currentStep === expectedSequence.length) {
      await onTrigger();
      resetState();
    } else {
      // Start/reset the inactivity timer
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => resetState(), resetTimeoutMs);
    }
  }
  return {
    registerGesture,
    getContext() {
      return { currentStep };
    },
  };
}
